import React, {useCallback, useMemo} from 'react';
import {FormikHelpers} from 'formik';
import {ContentStart, WebLinkFooter, WebLinkHeader} from './components';
import {Form} from 'forms';
import {FormNameEnum} from '../enums';
import {useCreateWebLink} from './hooks';
import {addWebLinkFormBaseValidationSchema, IAddWebLinkFormBaseValues, mapCreateResourceWebLink} from 'admin/resources';
import {PageLayoutOneVerticalCol} from 'layout/components';
import {usePermissions} from 'permissions';
import {SuperAndNationalAdminroles} from 'enums/admin-roles';
import {useLanguages} from '../hooks';
import {usePrefetchResourceMetadataLookups} from 'filters-selections';
import {runResourceFormSubmit, ResourceFormikBridge, useResourceUploadFormLifecycle} from 'admin/resources/utils';

export const UploadResourceWebLink = () => {
  const {createWebLink, submitStatus} = useCreateWebLink();
  const {hasPermission} = usePermissions();
  const {englishId} = useLanguages();
  const hasAdminPermission = hasPermission(SuperAndNationalAdminroles);

  usePrefetchResourceMetadataLookups();

  const initialValues = useMemo(
    () => ({
      [FormNameEnum.isAccessibleToStudents]: null,
      [FormNameEnum.hasUploadPermissions]: hasAdminPermission ? true : false,
      [FormNameEnum.displayName]: null,
      [FormNameEnum.keyTerms]: null,
      [FormNameEnum.programs]: [],
      [FormNameEnum.sessions]: [],
      [FormNameEnum.pillars]: [],
      [FormNameEnum.competencies]: [],
      [FormNameEnum.gradeLevels]: [],
      [FormNameEnum.languages]: englishId ? [englishId] : [],
      [FormNameEnum.skillsAndBehaviors]: [],
      [FormNameEnum.briefDescription]: '',
      [FormNameEnum.learningObjectives]: null,
      [FormNameEnum.expirationDate]: null,
      [FormNameEnum.knowledgeSkillsAndAbilities]: [],
      [FormNameEnum.webLink]: null,
    }),
    [hasAdminPermission, englishId]
  );

  const {formKey, capturePreSubmit, registerFormik} = useResourceUploadFormLifecycle<IAddWebLinkFormBaseValues>(
    initialValues as IAddWebLinkFormBaseValues,
    submitStatus,
    'webLink'
  );

  const submitHandler = useCallback(
    (formValues: IAddWebLinkFormBaseValues, helpers: FormikHelpers<IAddWebLinkFormBaseValues>) => {
      capturePreSubmit(formValues);
      const payload = mapCreateResourceWebLink(formValues);

      return runResourceFormSubmit(formValues, helpers, () => createWebLink(payload));
    },
    [createWebLink, capturePreSubmit]
  );

  return (
    <Form.Form<IAddWebLinkFormBaseValues>
      key={formKey}
      initialValues={initialValues as IAddWebLinkFormBaseValues}
      validationSchema={addWebLinkFormBaseValidationSchema}
      submitHandler={submitHandler}
      enableReinitialize={false}
      disableNativeSubmit
    >
      <ResourceFormikBridge registerFormik={registerFormik} />
      <PageLayoutOneVerticalCol
        header={<WebLinkHeader />}
        contentStart={<ContentStart />}
        footer={<WebLinkFooter />}
        withSecondaryNavigation
      />
    </Form.Form>
  );
};
